import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";
import log from '@/lib/logger';

const EMAIL_CONFIG_ADMIN_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/email-config`;
const EMAIL_CONFIG_CAPABILITY_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/email-config/capability`;

export interface ProviderSettingsSummary {
    apiUrl?: string;
    apiKeyConfigured: boolean;
    host?: string;
    port?: number;
    authenticationEnabled: boolean;
    usernameConfigured: boolean;
    passwordConfigured: boolean;
    encryption?: string;
}

export interface EmailConfigResponse {
    id: string;
    senderEmail: string;
    senderName: string;
    providerType: string;
    enabled: boolean;
    providerSettingsSummary: ProviderSettingsSummary;
    createdAt: string;
    updatedAt?: string;
    verifiedAt?: string | null;
}

export interface TestEmailConfigResponse {
    success: boolean;
    recipient: string;
    verifiedAt?: string | null;
    errorSummary?: string | null;
}

export interface EmailCapabilityResponse {
    available: boolean;
}

export interface ProviderSettingsRequest {
    apiUrl?: string;
    apiKey?: string;
    host?: string;
    port?: number;
    authentication?: boolean;
    username?: string;
    password?: string;
    encryption?: string;
}

export interface CreateEmailConfigRequest {
    senderEmail: string;
    senderName: string;
    providerType: string;
    providerSettings: ProviderSettingsRequest;
}

export interface UpdateEmailConfigRequest {
    senderEmail: string;
    senderName: string;
    providerType: string;
    providerSettings: ProviderSettingsRequest;
    enabled: boolean;
}

export async function getEmailCapability(token: string | null): Promise<EmailCapabilityResponse | void> {
    if (!token) {
        log.error("No token provided for email capability check.");
        return Promise.reject("No token provided");
    }

    const response = await fetch(EMAIL_CONFIG_CAPABILITY_URL, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    });

    return handleResponse<EmailCapabilityResponse>(response);
}

export async function deleteEmailConfig(token: string | null): Promise<void> {
    if (!token) {
        log.error("No token provided for email config deletion.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(EMAIL_CONFIG_ADMIN_URL, {
        method: 'DELETE',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
        }),
    });

    return handleResponse<void>(response);
}

export async function getEmailConfig(token: string | null): Promise<EmailConfigResponse | null | void> {
    if (!token) {
        log.error("No token provided for email config retrieval.");
        return Promise.reject("No token provided");
    }

    const response = await fetch(EMAIL_CONFIG_ADMIN_URL, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    });

    if (response.status === 204) {
        return null;
    }

    return handleResponse<EmailConfigResponse>(response);
}

export async function createEmailConfig(
    token: string | null,
    request: CreateEmailConfigRequest
): Promise<EmailConfigResponse | void> {
    if (!token) {
        log.error("No token provided for email config creation.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(EMAIL_CONFIG_ADMIN_URL, {
        method: 'POST',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });

    return handleResponse<EmailConfigResponse>(response);
}

export interface TestDraftEmailConfigRequest {
    senderEmail: string;
    senderName: string;
    providerType: string;
    providerSettings: ProviderSettingsRequest;
}

export async function testDraftEmailConfig(
    token: string | null,
    request: TestDraftEmailConfigRequest
): Promise<TestEmailConfigResponse | void> {
    if (!token) {
        log.error("No token provided for email config draft test.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${EMAIL_CONFIG_ADMIN_URL}/test-draft`, {
        method: 'POST',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });

    return handleResponse<TestEmailConfigResponse>(response);
}

export async function updateEmailConfig(
    token: string | null,
    request: UpdateEmailConfigRequest
): Promise<EmailConfigResponse | void> {
    if (!token) {
        log.error("No token provided for email config update.");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(EMAIL_CONFIG_ADMIN_URL, {
        method: 'PUT',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });

    return handleResponse<EmailConfigResponse>(response);
}
